class AuthService {
  constructor() {
    this.baseURL = (process.env.REACT_APP_API_URL || "https://api.oekfb.eu").replace(/\/$/, "");
  }

  setCookie(name, value, { days = 7, path = "/", sameSite = "Strict" } = {}) {
    const maxAge = days * 24 * 60 * 60;
    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      `path=${path}`,
      `max-age=${maxAge}`,
      `SameSite=${sameSite}`,
    ];
    if (window.location.protocol === "https:") parts.push("Secure");
    document.cookie = parts.join("; ");
  }

  setJSONCookie(name, value, options) {
    this.setCookie(name, JSON.stringify(value), options);
  }

  getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const part = document.cookie.split("; ").find((item) => item.startsWith(prefix));
    return part ? decodeURIComponent(part.slice(prefix.length)) : null;
  }

  getJSONCookie(name) {
    const raw = this.getCookie(name);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setSessionJSON(key, value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionJSON(key) {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  buildParsedTeams(teams = []) {
    return teams.map((team) => ({
      teamname: team?.team_name ?? team?.name ?? team?.teamName ?? null,
      TeamID: team?.id ?? team?.team_id ?? team?.teamId ?? null,
      "League Code": team?.league_code ?? team?.leagueCode ?? team?.league?.code ?? null,
      "League ID": team?.league ?? team?.league_id ?? team?.leagueId ?? null,
    }));
  }

  setTeamsParsed(teams) {
    this.setSessionJSON("teams_parsed", teams);
    this.setJSONCookie("teams_parsed", teams, { days: 7 });
  }

  getTeamsParsedJSON() {
    return this.getSessionJSON("teams_parsed") ?? this.getJSONCookie("teams_parsed") ?? [];
  }

  getPrimaryTeamParsed() {
    return this.getTeamsParsedJSON()[0] || null;
  }

  getTeamID() {
    return this.getCookie("teamID");
  }

  getAuthToken() {
    return this.getCookie("authToken");
  }

  isAuthenticated() {
    return Boolean(this.getTeamID() && this.getAuthToken());
  }

  logoutUser() {
    this.setCookie("authToken", "", { days: -1 });
    this.setCookie("teamID", "", { days: -1 });
    this.setCookie("teams_parsed", "", { days: -1 });
    sessionStorage.removeItem("teams_parsed");
  }

  encodeBasicCredentials(email, password) {
    const bytes = new TextEncoder().encode(`${email}:${password}`);
    let binary = "";
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary);
  }

  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/app/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Basic ${this.encodeBasicCredentials(email, password)}`,
        },
        credentials: "include",
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, message: data.reason || data.message || "Login failed" };
      }

      if (!data.token || !Array.isArray(data.teams) || data.teams.length === 0) {
        return { success: false, message: "No team is associated with this account." };
      }

      const primaryTeamID = data.teams[0]?.id;
      if (!primaryTeamID) {
        return { success: false, message: "The team identifier is missing." };
      }

      this.setCookie("teamID", primaryTeamID, { days: 7 });
      this.setCookie("authToken", data.token, { days: 7 });
      this.setTeamsParsed(this.buildParsedTeams(data.teams));

      return { success: true, user: data.user, team: data.teams[0] };
    } catch {
      return { success: false, message: "An error occurred while logging in" };
    }
  }
}

export default AuthService;
